// Alliance settings (spec §3.6): the MOTD from Warlord; profile, recruitment, emblem, rename and
// disband for the Overlord — the ranks the server accepts for each change.
import {
  ALLIANCE_EMBLEM_COLORS,
  ALLIANCE_EMBLEM_ICONS,
  type AllianceEmblem,
  type MemberAlliance,
} from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { endpoints } from '../../api/endpoints';
import { ApiRequestError } from '../../api/http';
import { Button, Checkbox, ErrorNotice, Notice, Panel, TextField } from '../ui';
import { EmblemPicker } from './EmblemPicker';
import { ConfirmButton, renameBlockedUntil, useRefresh } from './shared';

function MotdForm({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const refresh = useRefresh(galaxyId);
  const [motd, setMotd] = useState(alliance.motd);
  const save = useMutation({
    mutationFn: () => endpoints.updateAlliance(galaxyId, alliance.id, { motd: motd.trim() }),
    onSuccess: refresh,
  });
  return (
    <Panel title="Message of the day">
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <textarea
          aria-label="Message of the day"
          value={motd}
          maxLength={500}
          rows={3}
          onChange={(e) => setMotd(e.target.value)}
          className="block w-full hud-cut-sm border border-line-strong bg-void p-3 text-base text-text outline-none focus:border-info"
        />
        {save.error ? <ErrorNotice error={save.error} /> : null}
        {save.isSuccess ? <Notice tone="success">Message saved.</Notice> : null}
        <Button type="submit" disabled={save.isPending}>
          Save message
        </Button>
      </form>
    </Panel>
  );
}

function ProfileForm({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const refresh = useRefresh(galaxyId);
  const [description, setDescription] = useState(alliance.description);
  const save = useMutation({
    mutationFn: (body: { description?: string; openRecruitment?: boolean }) =>
      endpoints.updateAlliance(galaxyId, alliance.id, body),
    onSuccess: refresh,
  });
  return (
    <Panel title="Profile">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ description: description.trim() });
        }}
      >
        <TextField
          label="Description"
          value={description}
          maxLength={500}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Checkbox
          label="Accept applications from any commander"
          checked={alliance.openRecruitment}
          disabled={save.isPending}
          onChange={(e) => save.mutate({ openRecruitment: e.target.checked })}
        />
        {save.error ? <ErrorNotice error={save.error} /> : null}
        <Button type="submit" variant="secondary" disabled={save.isPending}>
          Save description
        </Button>
      </form>
    </Panel>
  );
}

function EmblemForm({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const refresh = useRefresh(galaxyId);
  const [emblem, setEmblem] = useState<AllianceEmblem>(
    alliance.emblem ?? { icon: ALLIANCE_EMBLEM_ICONS[0], color: ALLIANCE_EMBLEM_COLORS[0] }
  );
  const save = useMutation({
    mutationFn: () => endpoints.updateAlliance(galaxyId, alliance.id, { emblem }),
    onSuccess: refresh,
  });
  return (
    <Panel title="Emblem">
      <EmblemPicker value={emblem} tag={alliance.tag} onChange={setEmblem} />
      {save.error ? <ErrorNotice error={save.error} /> : null}
      {save.isSuccess ? <Notice tone="success">Emblem saved.</Notice> : null}
      <Button
        className="mt-3"
        variant="secondary"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        Save emblem
      </Button>
    </Panel>
  );
}

/** G-64: name and tag, once per alliance.rename_cooldown_h (168 h placeholder → "7 days" copy). */
function RenameForm({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const refresh = useRefresh(galaxyId);
  const [name, setName] = useState(alliance.name);
  const [tag, setTag] = useState(alliance.tag);
  const rename = useMutation({
    mutationFn: () =>
      endpoints.updateAlliance(galaxyId, alliance.id, {
        ...(name.trim() !== alliance.name ? { name: name.trim() } : {}),
        ...(tag !== alliance.tag ? { tag } : {}),
      }),
    onSuccess: refresh,
  });
  const changed = name.trim() !== alliance.name || tag !== alliance.tag;
  const error = rename.error;
  const fromError =
    error instanceof ApiRequestError && error.code === 'COOLDOWN_ACTIVE'
      ? (error.details?.allowedAt as string | undefined)
      : undefined;
  // The member view says when the next rename is allowed; the error is the fallback.
  const blockedUntil =
    renameBlockedUntil(alliance, Date.now()) ?? (fromError ? new Date(fromError) : null);
  return (
    <Panel title="Name and tag">
      <div className="space-y-3">
        <TextField
          label="Alliance name"
          value={name}
          maxLength={30}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Alliance tag"
          value={tag}
          maxLength={5}
          onChange={(e) => setTag(e.target.value.toUpperCase())}
        />
        <p className="text-xs text-muted">You can rename the alliance once every 7 days.</p>
        {blockedUntil ? (
          <Notice>You can rename again on {blockedUntil.toLocaleString()}.</Notice>
        ) : error ? (
          <ErrorNotice error={error} />
        ) : null}
        <ConfirmButton
          label="Rename alliance"
          question={`Rename to ${name.trim()} [${tag}]? You cannot rename again for 7 days.`}
          confirmLabel="Rename"
          disabled={!changed || rename.isPending || blockedUntil !== null}
          onConfirm={() => rename.mutate()}
        />
      </div>
    </Panel>
  );
}

function DisbandPanel({ galaxyId, alliance }: { galaxyId: string; alliance: MemberAlliance }) {
  const refresh = useRefresh(galaxyId);
  const disband = useMutation({
    mutationFn: () => endpoints.disbandAlliance(galaxyId, alliance.id),
    onSuccess: refresh,
  });
  return (
    <Panel title="Disband">
      {disband.error ? <ErrorNotice error={disband.error} /> : null}
      <ConfirmButton
        label="Disband alliance"
        question="Disband the alliance? Every member leaves, and reinforcements between members fly home."
        confirmLabel="Disband"
        disabled={disband.isPending}
        onConfirm={() => disband.mutate()}
      />
    </Panel>
  );
}

export function SettingsTab({
  galaxyId,
  alliance,
}: {
  galaxyId: string;
  alliance: MemberAlliance;
}) {
  const overlord = alliance.myRank === 'overlord';
  return (
    <div className="space-y-4">
      <MotdForm galaxyId={galaxyId} alliance={alliance} />
      {overlord ? <ProfileForm galaxyId={galaxyId} alliance={alliance} /> : null}
      {overlord ? <EmblemForm galaxyId={galaxyId} alliance={alliance} /> : null}
      {overlord ? <RenameForm galaxyId={galaxyId} alliance={alliance} /> : null}
      {overlord ? <DisbandPanel galaxyId={galaxyId} alliance={alliance} /> : null}
    </div>
  );
}
